import React from 'react';
import { useLockBaseline, useProjectBaseline } from '../../../services/api';

const SPARK_COLORS = [
  'var(--wf-g1)',
  'var(--wf-g2)',
  'var(--wf-ok)',
  'var(--wf-discover)',
  'var(--wf-understand)',
  'var(--wf-operate)',
];

/** Confetti sparks from the center of `el`; colors resolve against the Aurora scope. */
function burst(el: HTMLElement): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const scope = el.closest('.wf-aurora') ?? document.body;
  const styles = getComputedStyle(scope);
  const resolve = (c: string) => styles.getPropertyValue(c.slice(4, -1)).trim() || c;
  const r = el.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  for (let i = 0; i < 28; i++) {
    const s = document.createElement('span');
    s.className = 'wf-spark';
    s.style.left = `${cx}px`;
    s.style.top = `${cy}px`;
    s.style.background = resolve(SPARK_COLORS[i % SPARK_COLORS.length]);
    if (i % 3 === 0) s.style.borderRadius = '50%';
    document.body.appendChild(s);
    const a = Math.random() * Math.PI * 2;
    const d = 40 + Math.random() * 90;
    const dx = Math.cos(a) * d;
    const dy = Math.sin(a) * d - 20;
    s.animate(
      [
        { transform: 'translate(-50%,-50%) scale(1) rotate(0)', opacity: 1 },
        {
          transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + 30}px)) scale(0) rotate(${Math.random() * 540}deg)`,
          opacity: 0,
        },
      ],
      { duration: 700 + Math.random() * 600, easing: 'cubic-bezier(.15,.7,.3,1)' },
    ).onfinish = () => s.remove();
  }
}

/** Discover tab-bar action — freezes the current inventory as the project baseline. */
const BaselineLockButton: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { data: baseline, isLoading } = useProjectBaseline(projectId);
  const lockBaseline = useLockBaseline(projectId);
  const locked = Boolean(baseline);

  return (
    <button
      type="button"
      className={`wf-lock${locked ? ' done' : ''}`}
      disabled={locked || isLoading || lockBaseline.isPending}
      onClick={(e) => {
        const btn = e.currentTarget;
        lockBaseline.mutate({}, { onSuccess: () => burst(btn) });
      }}
      title={lockBaseline.isError ? 'Could not lock the baseline. Try again.' : undefined}
    >
      <svg viewBox="0 0 24 24" aria-hidden>
        {locked ? (
          <path d="m5 12.5 4.5 4.5L19 7.5" />
        ) : (
          <>
            <rect x="5" y="11" width="14" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </>
        )}
      </svg>
      {lockBaseline.isPending ? 'Locking…' : locked ? 'Baseline locked' : 'Lock baseline'}
    </button>
  );
};

export default BaselineLockButton;
